import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckSquare,
  ChevronDown,
  Filter,
  MoreVertical,
  Plus,
  Search,
  Settings2,
  ArrowUpDown,
  Calendar,
  User,
  Check,
  Trash2,
  X,
  Lock,
  Download,
  History,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../../../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { cn } from '../../../lib/utils';
import {
  ALL_TABLE_FIELDS,
  DEFAULT_TABLE_FIELDS,
  DUE_DATE_QUICK_OPTIONS,
  FILTER_FIELDS,
  LOCKED_FIELDS,
  MAX_DESCRIPTION_LENGTH,
  MAX_TITLE_LENGTH,
  SORT_OPTIONS,
  TASK_VIEWS,
  operatorsForFieldType,
} from '../constants/taskFields';
import {
  buildCreatePayload,
  cancelBulkJob,
  completeTask,
  createBulkJob,
  createTask,
  deleteTask,
  duplicateTask,
  fetchAssignees,
  fetchAssociationOptions,
  fetchBulkJobs,
  fetchTaskPreferences,
  fetchTasks,
  pauseBulkJob,
  reopenTask,
  resumeBulkJob,
  saveTaskPreferences,
  updateTask,
} from '../services/taskApi';
import {
  dueDateColumnLabel,
  formatTaskDueAt,
  getTimezoneAbbreviation,
  newIdempotencyKey,
  plainTextFromHtml,
} from '../services/taskTimezoneUtils';
import { TaskDrawer } from './TaskDrawer';
import { TaskAdvancedFilterDrawer, countActiveAdvancedFilters } from './TaskAdvancedFilterDrawer';
import { TaskManageFieldsDrawer } from './TaskManageFieldsDrawer';
import {
  TaskDateCalendar,
  formatDueRangeLabel,
  formatDisplayDate,
} from './TaskDateCalendar';

function hasPermission(workspace, key) {
  const perms = workspace?.permissions || [];
  if (perms.includes('tasks.manage_all')) return true;
  if (!perms.length) return true; // default allow until fine-grained UI perms are loaded
  return perms.includes(key);
}

function parseDueValueParam(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {
    /* plain date string for on/before/after */
  }
  return raw;
}

function serializeDueValue(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

export function TasksWorkspace({ workspace }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ all: 0, due_today: 0, overdue: 0, upcoming: 0 });
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(Number(searchParams.get('page') || 1));
  const [pageSize, setPageSize] = useState(Number(searchParams.get('page_size') || 20));
  const [view, setView] = useState(searchParams.get('view') || 'all');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [assigneeUserId, setAssigneeUserId] = useState(searchParams.get('assignee') || '');
  const [dueDateOperator, setDueDateOperator] = useState(searchParams.get('due') || '');
  const [dueDateValue, setDueDateValue] = useState(() => parseDueValueParam(searchParams.get('due_value')));
  const [dueRangeOpen, setDueRangeOpen] = useState(false);
  const [sortField, setSortField] = useState(searchParams.get('sort_field') || 'due_at');
  const [sortDirection, setSortDirection] = useState(searchParams.get('sort_direction') || 'asc');
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [tableFields, setTableFields] = useState(DEFAULT_TABLE_FIELDS);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [assigneeSearch, setAssigneeSearch] = useState('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [unsavedConfirm, setUnsavedConfirm] = useState(false);
  const [pendingClose, setPendingClose] = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(null);

  const timezone = workspace.timezone || 'UTC';
  const dueLabel = dueDateColumnLabel(timezone);
  const tzAbbr = getTimezoneAbbreviation(new Date(), timezone);

  const dueToolbarLabel = useMemo(() => {
    const base = DUE_DATE_QUICK_OPTIONS.find((o) => o.value === dueDateOperator)?.label || 'Any date';
    if (dueDateOperator === 'between' && dueDateValue?.start && dueDateValue?.end) {
      return `Between ${formatDueRangeLabel(dueDateValue.start, dueDateValue.end)}`;
    }
    if (dueDateOperator === 'on' && typeof dueDateValue === 'string' && dueDateValue) {
      return `On ${formatDisplayDate(dueDateValue)}`;
    }
    return base;
  }, [dueDateOperator, dueDateValue]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (view && view !== 'all') next.set('view', view); else next.delete('view');
    if (debouncedSearch) next.set('search', debouncedSearch); else next.delete('search');
    if (status) next.set('status', status); else next.delete('status');
    if (assigneeUserId) next.set('assignee', assigneeUserId); else next.delete('assignee');
    if (dueDateOperator) next.set('due', dueDateOperator); else next.delete('due');
    const serialized = serializeDueValue(dueDateValue);
    if (dueDateOperator && serialized) next.set('due_value', serialized); else next.delete('due_value');
    next.set('sort_field', sortField);
    next.set('sort_direction', sortDirection);
    next.set('page', String(page));
    next.set('page_size', String(pageSize));
    setSearchParams(next, { replace: true });
  }, [view, debouncedSearch, status, assigneeUserId, dueDateOperator, dueDateValue, sortField, sortDirection, page, pageSize]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTasks(workspace, {
        view,
        search: debouncedSearch,
        status: status || undefined,
        assigneeUserId: assigneeUserId || undefined,
        dueDateOperator: dueDateOperator || undefined,
        dueDateValue: dueDateValue || undefined,
        advancedFilters,
        sortField,
        sortDirection,
        page,
        pageSize,
      });
      setItems(data.items || []);
      setTotalItems(data.total_items || 0);
      setTotalPages(data.total_pages || 1);
      setSummary(data.summary || summary);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || 'Failed to load tasks');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [workspace, view, debouncedSearch, status, assigneeUserId, dueDateOperator, dueDateValue, advancedFilters, sortField, sortDirection, page, pageSize]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    fetchTaskPreferences(workspace)
      .then((data) => {
        if (Array.isArray(data.tableFields) && data.tableFields.length) {
          setTableFields(data.tableFields);
        }
      })
      .catch(() => {});
  }, [workspace]);

  useEffect(() => {
    fetchAssignees(workspace, { search: assigneeSearch })
      .then((data) => setAssignees(data.items || []))
      .catch(() => setAssignees([]));
  }, [workspace, assigneeSearch]);

  useEffect(() => {
    setSelectedIds([]);
    setSelectAllMatching(false);
  }, [view, debouncedSearch, status, assigneeUserId, dueDateOperator, dueDateValue, advancedFilters]);

  const activeAdvancedFilterCount = countActiveAdvancedFilters(advancedFilters);

  const hasActiveFilters = Boolean(
    debouncedSearch || status || assigneeUserId || dueDateOperator || activeAdvancedFilterCount || (view && view !== 'all'),
  );

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setStatus('');
    setAssigneeUserId('');
    setDueDateOperator('');
    setDueDateValue(null);
    setAdvancedFilters([]);
    setView('all');
    setPage(1);
  };

  const applyDueQuickOption = (optValue) => {
    if (optValue === 'between') {
      setDueRangeOpen(true);
      return;
    }
    setDueDateOperator(optValue);
    setDueDateValue(null);
    setPage(1);
  };

  const openCreate = () => {
    setEditingTask(null);
    setDrawerOpen(true);
  };

  const openEdit = (task) => {
    setEditingTask(task);
    setDrawerOpen(true);
  };

  const requestCloseDrawer = (dirty) => {
    if (dirty) {
      setPendingClose('drawer');
      setUnsavedConfirm(true);
      return;
    }
    setDrawerOpen(false);
    setEditingTask(null);
  };

  const toggleSelect = (taskId) => {
    setSelectAllMatching(false);
    setSelectedIds((prev) => (
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    ));
  };

  const togglePageSelect = () => {
    const pageIds = items.map((t) => t.taskId);
    const allSelected = pageIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const runBulk = async (operation, parameters = {}) => {
    try {
      const payload = {
        operation,
        selection: selectAllMatching
          ? {
              mode: 'filter',
              filter_snapshot: {
                view,
                status,
                search: debouncedSearch,
                assignee_user_id: assigneeUserId,
                due_date_operator: dueDateOperator,
                due_date_value: dueDateValue,
                advanced_filters: advancedFilters,
              },
              exclude_task_ids: [],
            }
          : { mode: 'task_ids', task_ids: selectedIds },
        parameters,
        idempotency_key: newIdempotencyKey(),
      };
      await createBulkJob(workspace, payload);
      toast.success('Bulk action started');
      setSelectedIds([]);
      setSelectAllMatching(false);
      setBulkConfirm(null);
      loadTasks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk action failed');
    }
  };

  const countLabel = `${totalItems} ${totalItems === 1 ? 'Task' : 'Tasks'}`;

  const visibleColumns = tableFields.filter((key) => key !== 'actions' || true);

  return (
    <div className="flex h-full min-h-0 flex-col text-foreground" data-testid="tasks-workspace">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-4 sm:px-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">Tasks</h1>
          <Badge variant="secondary" className="rounded-full bg-primary/10 text-primary" data-testid="tasks-count-badge">
            {countLabel}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {hasPermission(workspace, 'tasks.create') ? (
            <Button onClick={openCreate} data-testid="add-task-button">
              <Plus className="mr-1.5 h-4 w-4" />
              Add Task
            </Button>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Tasks menu">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(workspace.routes.bulkActions)}>
                <History className="mr-2 h-4 w-4" />
                Bulk actions
              </DropdownMenuItem>
              {hasPermission(workspace, 'tasks.export') ? (
                <DropdownMenuItem
                  onClick={() => setBulkConfirm({ operation: 'export', label: 'Export visible tasks' })}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Export visible tasks
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2 sm:px-6">
        {TASK_VIEWS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => { setView(tab.id); setPage(1); }}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              view === tab.id
                ? 'bg-primary/10 text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground',
            )}
            data-testid={`task-view-${tab.id}`}
          >
            {tab.label}
            {summary?.[tab.id === 'all' ? 'all' : tab.id] != null ? (
              <span className="ml-1 text-xs opacity-70">
                ({summary[tab.id === 'all' ? 'all' : tab.id]})
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3 sm:px-6">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5">
              <User className="h-3.5 w-3.5" />
              Assignee: {assigneeUserId === '__me__' ? 'Me' : assigneeUserId === '__unassigned__' ? 'Unassigned' : assigneeUserId ? 'Selected' : 'Any'}
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-64" align="start">
            <div className="p-2">
              <Input
                placeholder="Search team members"
                value={assigneeSearch}
                onChange={(e) => setAssigneeSearch(e.target.value)}
              />
            </div>
            <DropdownMenuItem onClick={() => { setAssigneeUserId(''); setPage(1); }}>Any</DropdownMenuItem>
            <DropdownMenuItem onClick={() => { setAssigneeUserId('__me__'); setPage(1); }}>Me</DropdownMenuItem>
            <DropdownMenuSeparator />
            {assignees.map((user) => (
              <DropdownMenuItem
                key={user.userId}
                onClick={() => { setAssigneeUserId(user.userId); setPage(1); }}
              >
                {user.displayName}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { setAssigneeUserId('__unassigned__'); setPage(1); }}>
              Unassigned
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5">
              <Check className="h-3.5 w-3.5" />
              Status: {status === 'pending' ? 'Pending' : status === 'completed' ? 'Completed' : 'All'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {[
              { value: '', label: 'All' },
              { value: 'pending', label: 'Pending' },
              { value: 'completed', label: 'Completed' },
            ].map((opt) => (
              <DropdownMenuItem
                key={opt.value || 'all'}
                onClick={() => { setStatus(opt.value); setPage(1); }}
                className={cn(status === opt.value && 'text-primary')}
              >
                {opt.label}
                {status === opt.value ? <Check className="ml-auto h-4 w-4" /> : null}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="tasks-due-date-filter">
              <Calendar className="h-3.5 w-3.5" />
              Due Date: {dueToolbarLabel}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
            {DUE_DATE_QUICK_OPTIONS.map((opt) => (
              <DropdownMenuItem
                key={opt.value || 'any'}
                onSelect={(event) => {
                  if (opt.value === 'between') {
                    event.preventDefault();
                  }
                  applyDueQuickOption(opt.value);
                }}
                data-testid={`tasks-due-option-${opt.value || 'any'}`}
              >
                {opt.label}
                {dueDateOperator === opt.value ? <Check className="ml-auto h-4 w-4" /> : null}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <TaskDateCalendar
          mode="range"
          open={dueRangeOpen}
          onOpenChange={setDueRangeOpen}
          rangeValue={dueDateOperator === 'between' ? dueDateValue : null}
          timezoneLabel={tzAbbr}
          numberOfMonths={typeof window !== 'undefined' && window.innerWidth < 640 ? 1 : 2}
          onClear={() => {
            setDueDateOperator('');
            setDueDateValue(null);
            setPage(1);
          }}
          onConfirm={(range) => {
            setDueDateOperator('between');
            setDueDateValue(range);
            setPage(1);
          }}
        />

        <Button
          variant="outline"
          size="sm"
          className={cn('gap-1.5', activeAdvancedFilterCount && 'border-primary text-primary')}
          onClick={() => setFiltersOpen(true)}
        >
          <Filter className="h-3.5 w-3.5" />
          Filters{activeAdvancedFilterCount ? ` (${activeAdvancedFilterCount})` : ''}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5 border-primary/40 text-primary">
              <ArrowUpDown className="h-3.5 w-3.5" />
              Sort (1)
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {SORT_OPTIONS.map((opt) => (
              <DropdownMenuItem
                key={`${opt.field}-${opt.direction}`}
                onClick={() => {
                  setSortField(opt.field);
                  setSortDirection(opt.direction);
                  setPage(1);
                }}
              >
                {opt.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="relative min-w-[180px] flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search for task title"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            data-testid="tasks-search"
          />
        </div>

        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setFieldsOpen(true)}>
          <Settings2 className="h-3.5 w-3.5" />
          Manage fields
        </Button>
      </div>

      {selectedIds.length > 0 || selectAllMatching ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/40 px-4 py-2 sm:px-6">
          <span className="text-sm">
            {selectAllMatching ? `All ${totalItems} matching selected` : `${selectedIds.length} selected`}
          </span>
          {!selectAllMatching && totalItems > selectedIds.length ? (
            <Button variant="link" size="sm" onClick={() => setSelectAllMatching(true)}>
              Select all matching filtered results
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" onClick={() => { setSelectedIds([]); setSelectAllMatching(false); }}>
            Clear
          </Button>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setBulkConfirm({ operation: 'complete', label: 'Mark completed' })}>Mark completed</Button>
            <Button size="sm" variant="outline" onClick={() => setBulkConfirm({ operation: 'reopen', label: 'Mark pending' })}>Mark pending</Button>
            <Button size="sm" variant="outline" onClick={() => setBulkConfirm({ operation: 'delete', label: 'Delete tasks', destructive: true })}>Delete</Button>
          </div>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto">
        {loading ? (
          <div className="flex h-64 items-center justify-center text-muted-foreground" data-testid="tasks-loading">
            Loading tasks…
          </div>
        ) : error ? (
          <div className="flex h-64 flex-col items-center justify-center gap-3" data-testid="tasks-error">
            <p className="text-destructive">{typeof error === 'string' ? error : 'Failed to load tasks'}</p>
            <Button onClick={loadTasks}>Retry</Button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center gap-3 px-4 text-center" data-testid="tasks-empty">
            <CheckSquare className="h-10 w-10 text-muted-foreground" />
            {hasActiveFilters ? (
              <>
                <h2 className="text-lg font-semibold">No tasks match these filters</h2>
                <p className="max-w-md text-sm text-muted-foreground">
                  Adjust or clear the active filters to see more tasks.
                </p>
                <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>
              </>
            ) : (
              <>
                <h2 className="text-lg font-semibold">No tasks yet</h2>
                <p className="max-w-md text-sm text-muted-foreground">
                  No tasks in sight. Ready to create a fresh one?
                </p>
                <Button onClick={openCreate}>Add Task</Button>
              </>
            )}
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse text-sm" data-testid="tasks-table">
              <thead className="sticky top-0 z-10 bg-muted/80 backdrop-blur">
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="w-10 px-3 py-3">
                    <Checkbox
                      checked={items.length > 0 && items.every((t) => selectedIds.includes(t.taskId))}
                      onCheckedChange={togglePageSelect}
                      aria-label="Select page"
                    />
                  </th>
                  {visibleColumns.map((key) => {
                    const meta = ALL_TABLE_FIELDS.find((f) => f.key === key);
                    const label = key === 'due_at' ? dueLabel : meta?.label || key;
                    return (
                      <th key={key} className="px-3 py-3 font-medium whitespace-nowrap">
                        {label}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {items.map((task) => (
                  <tr
                    key={task.taskId}
                    className="border-b border-border hover:bg-muted/40"
                    data-testid={`task-row-${task.taskId}`}
                  >
                    <td className="px-3 py-3">
                      <Checkbox
                        checked={selectAllMatching || selectedIds.includes(task.taskId)}
                        onCheckedChange={() => toggleSelect(task.taskId)}
                        aria-label={`Select ${task.title}`}
                      />
                    </td>
                    {visibleColumns.map((key) => (
                      <td key={key} className="px-3 py-3 align-top">
                        {key === 'status' && (
                          <Badge variant={task.status === 'completed' ? 'secondary' : 'outline'}>
                            {task.status === 'completed' ? 'Completed' : 'Pending'}
                          </Badge>
                        )}
                        {key === 'title' && (
                          <button type="button" className="text-left font-medium hover:underline" onClick={() => openEdit(task)}>
                            {task.title}
                          </button>
                        )}
                        {key === 'description' && (
                          <span className="line-clamp-2 text-muted-foreground">
                            {task.descriptionText || '—'}
                          </span>
                        )}
                        {key === 'associated_contacts' && (
                          <span>
                            {(task.associations || [])
                              .filter((a) => a.objectType === 'contact')
                              .map((a) => a.displayName)
                              .join(', ') || '—'}
                          </span>
                        )}
                        {key === 'associated_opportunities' && (
                          <span>
                            {(task.associations || [])
                              .filter((a) => a.objectType === 'opportunity')
                              .map((a) => a.displayName)
                              .join(', ') || '—'}
                          </span>
                        )}
                        {key === 'associated_companies' && (
                          <span>
                            {(task.associations || [])
                              .filter((a) => a.objectType === 'company')
                              .map((a) => a.displayName)
                              .join(', ') || '—'}
                          </span>
                        )}
                        {key === 'assignee' && (
                          <span>
                            {task.assignee?.displayName || 'Unassigned'}
                            {task.assignee && !task.assignee.active ? (
                              <Badge className="ml-1" variant="outline">Inactive</Badge>
                            ) : null}
                          </span>
                        )}
                        {key === 'due_at' && formatTaskDueAt(task.dueAt, task.timezone || timezone)}
                        {key === 'priority' && (task.priority || 'normal')}
                        {key === 'record_id' && <span className="font-mono text-xs">{task.taskId}</span>}
                        {key === 'created_at' && formatTaskDueAt(task.createdAt, timezone)}
                        {key === 'updated_at' && formatTaskDueAt(task.updatedAt, timezone)}
                        {key === 'actions' && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" aria-label="Task actions">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEdit(task)}>Edit</DropdownMenuItem>
                              {task.status === 'completed' ? (
                                <DropdownMenuItem onClick={async () => { await reopenTask(workspace, task.taskId); toast.success('Task reopened'); loadTasks(); }}>
                                  Mark pending
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={async () => { await completeTask(workspace, task.taskId); toast.success('Task completed'); loadTasks(); }}>
                                  Mark completed
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={async () => { await duplicateTask(workspace, task.taskId); toast.success('Task duplicated'); loadTasks(); }}>
                                Duplicate
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={async () => {
                                  await deleteTask(workspace, task.taskId);
                                  toast.success('Task deleted');
                                  loadTasks();
                                }}
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 sm:px-6">
        <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
        <div className="flex items-center gap-2">
          <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
            <SelectTrigger className="w-[80px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[10, 20, 50, 100].map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      </div>

      <TaskDrawer
        open={drawerOpen}
        onOpenChange={(open) => {
          if (!open) requestCloseDrawer(false);
          else setDrawerOpen(true);
        }}
        workspace={workspace}
        task={editingTask}
        onRequestClose={requestCloseDrawer}
        onSaved={() => { setDrawerOpen(false); setEditingTask(null); loadTasks(); }}
      />

      <TaskAdvancedFilterDrawer
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filters={advancedFilters}
        timezone={timezone}
        onApply={(next) => { setAdvancedFilters(next); setPage(1); setFiltersOpen(false); }}
      />

      <TaskManageFieldsDrawer
        open={fieldsOpen}
        onOpenChange={setFieldsOpen}
        fields={tableFields}
        timezone={timezone}
        onApply={async (next) => {
          setTableFields(next);
          setFieldsOpen(false);
          try {
            await saveTaskPreferences(workspace, next);
            toast.success('Field preferences saved');
          } catch {
            toast.error('Could not save field preferences');
          }
        }}
      />

      <AlertDialog open={unsavedConfirm} onOpenChange={setUnsavedConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Closing will discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setUnsavedConfirm(false);
                setDrawerOpen(false);
                setEditingTask(null);
                setPendingClose(null);
              }}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(bulkConfirm)} onOpenChange={(open) => !open && setBulkConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{bulkConfirm?.label || 'Confirm bulk action'}</AlertDialogTitle>
            <AlertDialogDescription>
              {bulkConfirm?.destructive
                ? 'This action cannot be undone for the selected tasks.'
                : 'This will run as a background bulk job.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => runBulk(bulkConfirm.operation)}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default TasksWorkspace;
